import React, { useState } from "react"
import { alpha, Box, Divider, IconButton, Menu, MenuItem, Typography, useTheme } from "@mui/material"
import LanguageIcon from "@mui/icons-material/Language"

interface ChatBotSelectionMenuProps<T> {
  selectedItem: T
  setSelectedItem: (item: T) => void
  items: T[]
  getItemId: (item: T) => string | number
  getItemName: (item: T) => string
  title?: string
  icon?: React.ReactNode
}

export const ChatBotSelectionMenu = <T,>({
  selectedItem,
  setSelectedItem,
  items,
  getItemId,
  getItemName,
  title = "Choose an option",
  icon = <LanguageIcon fontSize="small" />
}: ChatBotSelectionMenuProps<T>) => {
  const { palette, typography } = useTheme()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setMenuAnchor(null)
  }

  return (
    <>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{
          sx: {
            mt: -3,
            backgroundColor: palette.primary.contrastText,
            "& .MuiMenuItem-root": {
              color: "#D4D4D4",
              fontSize: "14px",
              fontWeight: "normal",
              fontFamily: typography.fontFamily,
              letterSpacing: "0px",
              minHeight: "24px",
              py: 0.5,
              "&:hover": {
                backgroundColor: alpha(palette.primary.main, 0.2),
                color: palette.primary.main
              },
              "&.Mui-selected": {
                color: palette.primary.main
              }
            }
          }
        }}
      >
        <Typography sx={{ px: 2, pt: 0.5, pb: 1, color: "#E4E4E4", fontSize: 12 }}>{title}</Typography>
        <Divider sx={{ borderColor: "#323232", borderWidth: "1px", borderBottomWidth: "0px" }} />
        {items.map((item) => (
          <MenuItem
            key={getItemId(item)}
            selected={getItemId(item) === getItemId(selectedItem)}
            onClick={() => {
              setSelectedItem(item)
              handleCloseMenu()
            }}
          >
            {getItemName(item)}
          </MenuItem>
        ))}
      </Menu>

      <IconButton
        size="small"
        onClick={handleOpenMenu}
        sx={{
          color: "#797878",
          "&:hover": {
            color: palette.primary.main
          }
        }}
      >
        {icon}
      </IconButton>
    </>
  )
}
